import { isPresent } from "~/lib/isPresent";
import { getRequestContext } from "~/lib/requestContext";
import {
  InfiniteData,
  QueryKey,
  useInfiniteQuery as useBaseInfiniteQuery,
  useQuery as useBaseQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { concat, merge } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { apiClient, PaginatedResponse } from "..";
import { PaginatedQueryParams } from "../types";

export type FetchQueryFunctionArgs<Params> = {
  path: string;
  params?: Params | null;
  id?: number | string | null;
  resource?: string | Array<string | undefined>;
};

export const makeQueryKey = ({
  resource: resourceKey,
  id,
  params,
  infinite,
}: {
  resource?: string | Array<string | undefined>;
  id?: number | string | null;
  params?: unknown;
  infinite?: boolean;
}) =>
  concat(resourceKey, infinite && "infinite", id, params)
    .filter(Boolean)
    .filter(isPresent);

export const fetchQueryFunction = async <Params, Response>({
  path,
  resource,
  id,
  params,
}: FetchQueryFunctionArgs<Params>) => {
  const { queryClient } = getRequestContext();
  const { data } = await apiClient().get<Response>(path, { params });

  queryClient?.setQueryData(makeQueryKey({ resource, id, params }), data);
  return data;
};

export type PaginatedPreloadParams =
  | boolean
  | {
      paginated?: boolean;
      infinite?: boolean;
    };

const shouldPreload = (
  preload: PaginatedPreloadParams | undefined = true,
  type: "paginated" | "infinite",
) => {
  if (typeof preload === "boolean") return preload;
  return preload?.[type] === true;
};

export type PaginatedQueryFunction<
  Params extends PaginatedQueryParams,
  Response extends PaginatedResponse,
> = (params?: Params, preload?: PaginatedPreloadParams) => Promise<Response>;

export const fetchPaginatedQueryFunction = async <
  Params extends PaginatedQueryParams,
  Response extends PaginatedResponse,
>({
  path,
  resource,
  params: baseParams,
  preload,
}: FetchQueryFunctionArgs<Params> & { preload?: PaginatedPreloadParams }) => {
  const { queryClient } = getRequestContext();
  const params = { page: 1, ...baseParams };
  const { data } = await apiClient()
    .get<Response>(path, { params })
    .catch((err) => {
      if (process.env.NODE_ENV === "development" && isAxiosError(err)) {
        const base = err.config?.baseURL?.replace(/\/$/, "") ?? "";
        const rel = err.config?.url ?? path;
        console.error("[fetchPaginatedQueryFunction] API request failed", {
          path,
          method: err.config?.method,
          fullUrl: rel.startsWith("http")
            ? rel
            : `${base}/${rel.replace(/^\//, "")}`,
          params,
          status: err.response?.status,
          responseBody: err.response?.data,
        });
      }
      throw err;
    });
  const page = params.page;

  if (shouldPreload(preload, "paginated")) {
    queryClient?.setQueryData(makeQueryKey({ resource, params }), data);
  }

  if (shouldPreload(preload, "infinite")) {
    queryClient?.setQueryData(
      makeQueryKey({ resource, params: baseParams, infinite: true }),
      (old_data?: InfiniteData<Response, number>) => {
        const pages = old_data ? [...old_data.pages] : [];
        const pageParams = old_data ? [...old_data.pageParams] : [];
        pages[page - 1] = data;
        pageParams[page - 1] = page;
        return { pages, pageParams };
      },
    );
  }
  return data;
};

export const useInfiniteQueryData = <T>(
  data?: InfiniteData<PaginatedResponse<T>>,
) => useMemo(() => data?.pages?.flatMap((p) => p.records) || [], [data]);

export type InfiniteQueryOptions<T> = UseInfiniteQueryOptions<
  PaginatedResponse<T>,
  Error,
  InfiniteData<PaginatedResponse<T>, number>,
  PaginatedResponse<T>,
  QueryKey,
  number
>;

export type InfiniteQueryResult<Record> = UseInfiniteQueryResult<
  InfiniteData<PaginatedResponse<Record>, number>,
  Error
> & { all_records: Record[] };

export type UseInfiniteQueryFunction<
  Record,
  Params extends PaginatedQueryParams = PaginatedQueryParams,
> = (
  params?: Params,
  options?: Omit<
    InfiniteQueryOptions<Record>,
    "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
  >,
) => InfiniteQueryResult<Record>;

export const useInfiniteQuery = <
  Record,
  Params extends PaginatedQueryParams = PaginatedQueryParams,
>(
  resource: string | string[],
  queryFn: (params?: Params) => Promise<PaginatedResponse<Record>>,
  params?: Params,
  options?: Omit<
    InfiniteQueryOptions<Record>,
    "queryKey" | "queryFn" | "initialPageParam" | "getNextPageParam"
  >,
): InfiniteQueryResult<Record> => {
  const default_options: InfiniteQueryOptions<Record> = {
    queryKey: makeQueryKey({ resource, params, infinite: true }),
    queryFn: ({ pageParam = 1 }) => queryFn(merge({ page: pageParam }, params)),
    getNextPageParam: (lastPage) => lastPage.next_page,
    initialPageParam: 1,
  };
  const response = useBaseInfiniteQuery(merge(default_options, options));
  const all_records = useInfiniteQueryData<Record>(response.data);
  return useMemo(() => ({ all_records, ...response }), [response, all_records]);
};

export type PaginatedQueryOptions<Record> = UseQueryOptions<
  PaginatedResponse<Record>,
  Error
>;

export type UsePaginatedQueryResult<Record> = UseQueryResult<
  PaginatedResponse<Record>,
  Error
> & {
  records: Record[];
  /** Requested page (resets to 1 when filter/sort params change). */
  page: number;
  fetchNextPage: () => void;
  fetchPreviousPage: () => void;
  setPage: (page: number) => void;
};

export type UsePaginatedQueryFunction<
  T,
  Params extends PaginatedQueryParams = PaginatedQueryParams,
> = (
  params?: Params,
  options?: Omit<PaginatedQueryOptions<T>, "queryKey" | "queryFn">,
) => UsePaginatedQueryResult<T>;

export const usePaginatedQuery = <
  Record,
  Params extends PaginatedQueryParams = PaginatedQueryParams,
>(
  resource: string | string[],
  queryFn: (params?: Params) => Promise<PaginatedResponse<Record>>,
  params?: Params,
  options?: Omit<PaginatedQueryOptions<Record>, "queryKey" | "queryFn">,
): UsePaginatedQueryResult<Record> => {
  const paramsKey = JSON.stringify(params ?? null);
  const [pageState, setPage] = useState(params?.page || 1);
  const [prevParamsKey, setPrevParamsKey] = useState(paramsKey);
  const paramsChanged = paramsKey !== prevParamsKey;
  if (paramsChanged) {
    setPrevParamsKey(paramsKey);
    setPage(params?.page || 1);
  }
  const page = paramsChanged ? params?.page || 1 : pageState;
  const paginated_params = merge({}, params, { page });

  const default_options: PaginatedQueryOptions<Record> = {
    queryKey: makeQueryKey({ resource, params: paginated_params }),
    queryFn: () => queryFn(paginated_params),
  };
  const response = useBaseQuery(merge(default_options, options));
  const total_pages = useMemo(
    () => response.data?.total_pages || 1,
    [response?.data?.total_pages],
  );
  const nextPage = useCallback(() => {
    setPage((p) => (p < total_pages ? p + 1 : p));
  }, [total_pages]);
  const previousPage = useCallback(() => {
    setPage((p) => (p > 1 ? p - 1 : p));
  }, []);
  const setPageFn = useCallback(
    (next: number) => {
      setPage((current_page) =>
        next <= total_pages && next >= 1 ? next : current_page,
      );
    },
    [total_pages],
  );

  const records = useMemo(() => response.data?.records || [], [response.data]);
  return useMemo(
    () => ({
      records,
      ...response,
      page,
      fetchNextPage: nextPage,
      fetchPreviousPage: previousPage,
      setPage: setPageFn,
    }),
    [response, records, page, nextPage, previousPage, setPageFn],
  );
};
